/**
 * <pga-tour-central> — PGA Tour schedule, featured tournament and season calendar.
 *
 * Data: GET data-endpoint (the merchant's Cloudflare Worker) → { data: Tournament[] }.
 * The Worker holds the API key; this file never sends credentials.
 * The free API tier has schedules, courses and champions only, so nothing here
 * renders scores, rankings or leaderboards.
 *
 * Commands (buttons with commandfor="<this id>"):
 *   --detail      data-id + data-source → open/close that tournament's detail panel (never changes the featured one)
 *   --close-detail  close the open detail panel and return focus to its trigger
 *   --tab         data-tab → switch the panel tab
 *   --show-panel  focus the panel on its overview tab
 *   --toggle-calendar  expand/collapse the full season schedule
 *   --retry       refetch after an error
 */

const ICON_CALENDAR =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0"><rect x="3" y="4.5" width="14" height="12" rx="1"/><path d="M3 8.5h14M7 2.5v4M13 2.5v4"/></svg>';
const ICON_PIN =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0"><path d="M10 17.5s5.5-5 5.5-9.5a5.5 5.5 0 1 0-11 0c0 4.5 5.5 9.5 5.5 9.5Z"/><circle cx="10" cy="8" r="2"/></svg>';
const ICON_FLAG =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0"><path d="M5 17.5V2.5M5 3.5h9l-2 3 2 3H5"/></svg>';
const ICON_ARROW =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0 rtl:-scale-x-100"><path d="M4 10h12M11 5l5 5-5 5"/></svg>';

const ICON_STATUS =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0"><circle cx="10" cy="10" r="6.5"/></svg>';
const ICON_PURSE =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0"><rect x="2.5" y="5.5" width="15" height="9" rx="1"/><circle cx="10" cy="10" r="2"/><path d="M5.5 8v4M14.5 8v4"/></svg>';
const ICON_TROPHY =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-4 shrink-0"><path d="M6 3.5h8v4a4 4 0 0 1-8 0v-4ZM6 5H3.5v1.5A2.5 2.5 0 0 0 6 9M14 5h2.5v1.5A2.5 2.5 0 0 1 14 9M10 11.5v3M7 17.5h6M8 14.5h4v3H8z"/></svg>';

const STATUS_PILL = {
  ongoing: 'bg-interactive text-interactive-text',
  upcoming: 'bg-surface-secondary text-on-surface',
  completed: 'border border-border text-on-surface-secondary',
  canceled: 'border border-border text-on-surface-secondary line-through',
  unknown: 'border border-border text-on-surface-secondary',
};

// Worker responses are cached for 30 minutes at the edge; a slow network gets 12s before the error state.
const FETCH_TIMEOUT_MS = 12000;

// Decorative line-art for the upcoming tournament cards, picked from the API's city/state/country (see sceneFor).
// Built once from small drawing helpers on a 200×140 canvas anchored bottom-right, in currentColor;
// the card sets colour and a low opacity. `mass` paths are soft filled shapes that give the drawings body.
const r1 = (n) => Math.round(n * 10) / 10;

function quad(p0, c, p1, t) {
  const u = 1 - t;
  return [u * u * p0[0] + 2 * u * t * c[0] + t * t * p1[0], u * u * p0[1] + 2 * u * t * c[1] + t * t * p1[1]];
}

function quadTangent(p0, c, p1, t) {
  const x = 2 * (1 - t) * (c[0] - p0[0]) + 2 * t * (p1[0] - c[0]);
  const y = 2 * (1 - t) * (c[1] - p0[1]) + 2 * t * (p1[1] - c[1]);
  const m = Math.hypot(x, y) || 1;
  return [x / m, y / m];
}

// A drooping feathered palm frond: curved spine plus leaflets that shorten toward the tip.
function frond(x, y, deg, len, droop = 0.35) {
  const a = (deg * Math.PI) / 180;
  const end = [x + Math.cos(a) * len, y + Math.sin(a) * len + droop * len * 0.6];
  const c = [x + Math.cos(a) * len * 0.6, y + Math.sin(a) * len * 0.6 - len * 0.12];
  let d = `M${r1(x)} ${r1(y)}Q${r1(c[0])} ${r1(c[1])} ${r1(end[0])} ${r1(end[1])}`;
  for (let i = 1; i <= 11; i++) {
    const t = i / 12;
    const p = quad([x, y], c, end, t);
    const [tx, ty] = quadTangent([x, y], c, end, t);
    const leaf = len * 0.3 * (1 - t * 0.65);
    [1, -1].forEach((side) => {
      const ang = side * 0.95;
      let dx = tx * Math.cos(ang) - ty * Math.sin(ang);
      let dy = tx * Math.sin(ang) + ty * Math.cos(ang) + 0.55;
      const m = Math.hypot(dx, dy) || 1;
      dx /= m;
      dy /= m;
      d += `M${r1(p[0])} ${r1(p[1])}l${r1(dx * leaf)} ${r1(dy * leaf)}`;
    });
  }
  return d;
}

function palm(bx, by, tx, ty, size) {
  const c = [bx + (tx - bx) * 0.15, (by + ty) / 2];
  let d = '';
  [-1, 1].forEach((s) => {
    d += `M${r1(bx + s * 3.2)} ${by}Q${r1(c[0] + s * 2.4)} ${r1(c[1])} ${r1(tx + s * 1.6)} ${r1(ty)}`;
  });
  for (let t = 0.06; t < 0.96; t += 0.07) {
    const p = quad([bx, by], c, [tx, ty], t);
    const w = 3.2 - 1.6 * t;
    d += `M${r1(p[0] - w)} ${r1(p[1] + 0.8)}q${r1(w)} -1.6 ${r1(w * 2)} 0`;
  }
  [192, 214, 238, 262, 288, 314, 338, 358].forEach((deg, i) => {
    d += frond(tx, ty, deg, size * (i % 2 ? 0.85 : 1), 0.3 + (i % 3) * 0.08);
  });
  return `<path d="${d}"/><circle cx="${tx - 2}" cy="${ty + 3}" r="2.2"/><circle cx="${tx + 2.5}" cy="${ty + 3.5}" r="2.2"/><circle cx="${tx}" cy="${ty + 6}" r="2"/>`;
}

function waves(y, x0, x1, amp, len) {
  let d = `M${x0} ${y}`;
  for (let x = x0; x < x1; x += len) d += `q${len / 4} ${-amp} ${len / 2} 0t${len / 2} 0`;
  return d;
}

function grass(x, y, h = 6) {
  return `M${x} ${y}l-2 ${-h}M${x} ${y}l0 ${-h - 2}M${x} ${y}l2.5 ${-h}`;
}

function cloudTree(cx, cy, r, trunk) {
  const pts = 9;
  let d = '';
  for (let i = 0; i <= pts; i++) {
    const a = Math.PI + (i / pts) * Math.PI * 2;
    const rr = r * (i % 2 ? 1 : 0.86);
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr * 0.92;
    d += i ? `A${r1(r * 0.38)} ${r1(r * 0.38)} 0 0 1 ${r1(x)} ${r1(y)}` : `M${r1(x)} ${r1(y)}`;
  }
  const inner = `M${r1(cx - r * 0.5)} ${r1(cy + r * 0.1)}q${r1(r * 0.3)} ${r1(-r * 0.35)} ${r1(r * 0.6)} ${r1(-r * 0.2)}M${r1(cx + r * 0.05)} ${r1(cy + r * 0.45)}q${r1(r * 0.3)} ${r1(-r * 0.25)} ${r1(r * 0.55)} 0`;
  const stem = `M${cx} ${r1(cy + r * 0.7)}V${r1(cy + r * 0.7 + trunk)}M${cx} ${r1(cy + r * 0.9)}l${r1(-r * 0.3)} ${r1(-r * 0.3)}M${cx} ${r1(cy + r * 1.05)}l${r1(r * 0.28)} ${r1(-r * 0.32)}`;
  return `<path d="${d}Z${inner}${stem}"/>`;
}

function flag(x, y, h, w = 18) {
  return `<path d="M${x} ${y}V${y - h}"/><path d="M${x} ${y - h}c${w * 0.35} 2 ${w * 0.65} -1 ${w} 3c${-w * 0.3} 3 ${-w * 0.6} 6 ${-w} 6.5"/><ellipse cx="${x}" cy="${y}" rx="4.5" ry="1.4"/>`;
}

function saguaro(x, y, h) {
  const top = y - h;
  let d = `M${x - 6} ${y}V${top + 6}a6 6 0 0 1 12 0V${y}`;
  d += `M${x - 2.5} ${top + 3}V${y}M${x + 2.5} ${top + 3}V${y}`;
  d += `M${x - 6} ${y - h * 0.42}h-7a5 5 0 0 1 -5 -5V${top + h * 0.28}a4.5 4.5 0 0 1 9 0v${h * 0.16}h3`;
  d += `M${x - 15.5} ${top + h * 0.26}V${y - h * 0.46}`;
  d += `M${x + 6} ${y - h * 0.58}h5a4.5 4.5 0 0 0 4.5 -4.5V${top + h * 0.16}a4.5 4.5 0 0 0 -9 0v${h * 0.12}h-0.5`;
  d += `M${x + 13} ${top + h * 0.14}V${y - h * 0.62}`;
  for (let yy = top + 10; yy < y - 4; yy += 9) d += `M${x - 6} ${yy}l-1.5 -1M${x + 6} ${yy + 4}l1.5 -1`;
  return `<path d="${d}"/>`;
}

const SCENES = {
  tropical: () =>
    `<path class="mass" d="M40 140c36-12 92-16 160-8v8Z"/>` +
    palm(132, 136, 120, 36, 34) +
    palm(172, 138, 178, 66, 26) +
    palm(92, 136, 98, 82, 18) +
    `<path d="${waves(128, 20, 80, 2.4, 14)}${waves(134, 30, 100, 2, 12)}"/>` +
    `<path d="M40 138c40-10 100-12 160-6"/><path d="M62 30q4-4 8 0q4-4 8 0M74 18q3-3 6 0q3-3 6 0"/>`,
  desert: () =>
    `<circle class="mass" cx="92" cy="44" r="13"/><circle cx="92" cy="44" r="13"/>` +
    `<path class="mass" d="M20 140l30-34 14 12 18-22 16 14 14-10 30 40Z"/>` +
    `<path d="M20 140l30-34 14 12 18-22 16 14 14-10 30 40M44 113l8 6M76 101l9 8M96 108l6 5"/>` +
    saguaro(146, 136, 92) +
    `<path d="M176 136v-18a6 6 0 0 1 12 0v18M182 112v24M172 124h-3a3 3 0 0 1-3-3v-5"/>` +
    `<path d="M114 136a7 6 0 0 1 14 0M118 131v5M124 131v5"/>` +
    `<path d="M10 137h190"/><path d="${grass(106, 137)}${grass(196, 137, 5)}${grass(60, 137, 4)}"/>` +
    `<path d="M70 22q3-3 6 0q3-3 6 0M100 14q2.5-2.5 5 0q2.5-2.5 5 0"/>`,
  coastal: () =>
    `<path class="mass" d="M120 134c10-12 50-14 70-4v10h-70Z"/>` +
    `<path d="M140 128l5-74h16l5 74Z"/><path d="M142 110h22M143 92h20M144 74h18"/>` +
    `<path d="M150 120h6v8h-6ZM151 98h4v6h-4ZM152 80h3v5h-3Z"/>` +
    `<path d="M140 54h26M141 50h24v4M143 50v4M147 50v4M151 50v4M155 50v4M159 50v4M163 50v4"/>` +
    `<path d="M145 50V38h16v12M145 38l8-9 8 9M153 29v-4"/>` +
    `<path d="M161 42l30-10M161 44l32 2M145 42l-24-8"/>` +
    `<path d="M114 132c8-8 18-10 28-6M150 128c12-8 30-8 46 2"/>` +
    `<path d="${waves(112, 10, 120, 3, 18)}${waves(122, 22, 130, 3, 16)}${waves(132, 6, 120, 2.6, 20)}"/>` +
    `<path d="M10 102h104"/><path d="M70 40q4-4 8 0q4-4 8 0M90 58q3-3 6 0q3-3 6 0M52 64q2.5-2.5 5 0q2.5-2.5 5 0"/>`,
  parkland: () =>
    `<path class="mass" d="M20 132c34-22 82-26 120-14s44 8 60 2v20H20Z"/>` +
    `<path d="M20 132c34-22 82-26 120-14s44 8 60 2"/><path d="M60 140c30-12 78-14 140-4"/>` +
    `<path d="M70 124c20-8 44-10 66-6M84 130c20-6 46-7 68-3"/>` +
    cloudTree(150, 58, 22, 30) +
    cloudTree(184, 80, 14, 22) +
    cloudTree(116, 86, 11, 16) +
    `<ellipse cx="94" cy="122" rx="16" ry="3.5"/>` +
    flag(94, 122, 34, 16) +
    `<path d="M50 126q8-4 16-1"/><path d="${grass(40, 134)}${grass(170, 134, 5)}"/>` +
    `<path d="M58 34q4-4 8 0q4-4 8 0"/>`,
  texas: () =>
    `<circle class="mass" cx="84" cy="54" r="12"/><circle cx="84" cy="54" r="12"/>` +
    `<path d="M84 36v-6M84 72v6M66 54h-6M102 54h6M71 41l-4-4M97 41l4-4M71 67l-4 4M97 67l4 4"/>` +
    `<path class="mass" d="M14 122h44l8-16h34l8 16h92v18H14Z"/>` +
    `<path d="M14 122h44l8-16h34l8 16h92M72 106v16M86 106v16"/>` +
    `<ellipse cx="156" cy="128" rx="26" ry="5.5"/><ellipse cx="120" cy="132" rx="9" ry="2.2"/>` +
    flag(156, 128, 70, 22) +
    `<path d="M20 136h180"/><path d="M24 136v-9M40 136v-9M24 130h16M56 136v-9M40 130h16"/>` +
    `<path d="${grass(110, 136)}${grass(190, 136, 5)}${grass(70, 136, 4)}"/>`,
  neutral: () =>
    `<ellipse class="mass" cx="148" cy="122" rx="42" ry="11"/>` +
    `<ellipse cx="148" cy="122" rx="42" ry="11"/><ellipse cx="148" cy="122" rx="54" ry="15"/><ellipse cx="148" cy="122" rx="30" ry="7.5"/>` +
    flag(156, 122, 74, 22) +
    `<circle cx="124" cy="120" r="3"/><path d="M122.5 119.5h.1M125 121h.1"/>` +
    cloudTree(70, 92, 12, 18) +
    cloudTree(96, 98, 9, 12) +
    `<path d="${grass(80, 134)}${grass(196, 132, 5)}"/><path d="M60 40q4-4 8 0q4-4 8 0"/>`,
};

const SCENE_CACHE = {};

function sceneSvg(scene) {
  const key = SCENES[scene] ? scene : 'neutral';
  SCENE_CACHE[key] ??= SCENES[key]();
  return `<svg viewBox="0 0 200 140" fill="none" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round" preserveAspectRatio="xMaxYMax meet" aria-hidden="true" focusable="false" class="size-full [&_.mass]:fill-current [&_.mass]:stroke-none [&_.mass]:opacity-25">${SCENE_CACHE[key]}</svg>`;
}

// Location → scene. Order matters: desert before tropical (Los Cabos is in Mexico), tropical before coastal.
// Any other US/Canadian venue reads as parkland; everything unrecognised gets the neutral flag and green.
function sceneFor(t) {
  const city = clean(t.city).toLowerCase();
  const state = clean(t.state).toLowerCase();
  const country = clean(t.country).toLowerCase();
  const all = `${city} | ${state} | ${country}`;
  if (/arizona|nevada|utah|new mexico|cabo|la quinta|palm springs|palm desert|scottsdale|las vegas|tucson|phoenix/.test(all)) return 'desert';
  if (/hawaii|mexico|bahamas|dominican|puerto rico|jamaica|barbados|cayman|aruba|caribbean|virgin islands|antigua|trinidad|st\.? lucia|cura[cç]ao|florida/.test(all)) return 'tropical';
  if (state === 'texas' || /\btx\b/.test(state)) return 'texas';
  if (/bermuda|scotland|england|ireland|wales|beach|island|\bbay\b|harbou?r|coast|shore|pebble|southampton|monterey|carlsbad|san diego|kiawah|hilton head|\bsea\b/.test(all)) return 'coastal';
  if (/^united states|^usa$|^us$|canada/.test(country)) return 'parkland';
  return 'neutral';
}

class PgaTourCentral extends HTMLElement {
  #ac;
  #t = {};
  #images = [];
  #scenes = {};
  #tournaments = [];
  #upcoming = [];
  #selectedId = null;
  #featuredReason = 'label_selected';
  #activeTab = 'overview';
  #calendarOpen = false;
  #detail = null;
  #nextId = null;
  #detailTimer;
  #locale = 'sv-SE';
  #live;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (!this.id) this.id = 'pga-tour-central';
    this.#locale = document.documentElement.lang || 'sv-SE';
    this.#readConfig();

    this.#live = document.createElement('div');
    this.#live.className = 'sr-only';
    this.#live.setAttribute('aria-live', 'polite');
    this.appendChild(this.#live);

    this.addEventListener('command', (e) => this.#onCommand(e), opts);
    const tablist = this.querySelector('[role="tablist"]');
    tablist?.addEventListener('keydown', (e) => this.#onTabKey(e), opts);

    this.#load();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // ---------- setup ----------

  #readConfig() {
    const node = this.querySelector('script[data-pga-config]');
    if (!node) return;
    try {
      const config = JSON.parse(node.textContent);
      this.#t = config.t || {};
      this.#images = Array.isArray(config.images) ? config.images.filter((i) => i && i.src) : [];
      this.#scenes = config.scenes && typeof config.scenes === "object" ? config.scenes : {};
    } catch {
      this.#t = {};
    }
  }

  #q(name) {
    return this.querySelector(`[data-pga="${name}"]`);
  }

  // ---------- data ----------

  async #load() {
    this.#renderLoading();
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return this.#renderError();

    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), FETCH_TIMEOUT_MS);
    this.#ac.signal.addEventListener('abort', () => timeout.abort(), { once: true });

    try {
      const res = await fetch(endpoint, { signal: timeout.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !Array.isArray(json.data)) throw new Error('Unexpected payload');

      this.#tournaments = json.data
        .filter((t) => t && typeof t.name === 'string' && isIsoDate(t.start_date))
        .map((t) => ({ ...t, _status: statusOf(t) }))
        .sort((a, b) => a.start_date.localeCompare(b.start_date));

      if (!this.#tournaments.length) return this.#renderEmpty();
      this.#render();
    } catch (err) {
      if (this.#ac.signal.aborted) return;
      console.warn('[pga-tour-central]', err);
      this.#renderError();
    } finally {
      clearTimeout(timer);
    }
  }

  #render() {
    const today = todayIso();
    const all = this.#tournaments;

    // Upcoming means the API says it hasn't started AND it hasn't ended by date, so stale rows never show as upcoming.
    this.#upcoming = all.filter((t) => t._status === 'upcoming' && (t.end_date || t.start_date) >= today);
    const ongoing = all.filter((t) => t._status === 'ongoing');
    const completed = all
      .filter((t) => t._status === 'completed')
      .sort((a, b) => (b.end_date || b.start_date).localeCompare(a.end_date || a.start_date));

    // The row the full calendar scrolls to and highlights: the tournament in play, otherwise the next one to start.
    this.#nextId = (ongoing[0] || this.#upcoming[0])?.id ?? null;

    let featured;
    if (ongoing.length) {
      featured = ongoing[0];
      this.#featuredReason = 'label_ongoing';
    } else if (this.#upcoming.length) {
      featured = this.#upcoming[0];
      this.#featuredReason = 'label_next';
    } else if (completed.length) {
      featured = completed[0];
      this.#featuredReason = 'label_latest';
    } else {
      featured = all[all.length - 1];
      this.#featuredReason = 'label_selected';
    }

    const showRecent = this.#upcoming.length === 0;
    this.#q('heading-upcoming')?.toggleAttribute('hidden', showRecent);
    this.#q('heading-recent')?.toggleAttribute('hidden', !showRecent);

    // The featured tournament already has the big card, so the row shows the four after it.
    const cards = (showRecent ? completed : this.#upcoming).filter((t) => t !== featured).slice(0, 4);
    this.#renderCards(cards);

    const season = all.find((t) => t.season)?.season;
    const seasonEl = this.#q('season');
    if (seasonEl && season) seasonEl.textContent = `${this.#t.season || ''} ${season}`.trim();

    const toggle = this.#q('schedule-toggle');
    if (toggle) toggle.hidden = false;
    if (this.#calendarOpen) this.#renderCalendar();

    const source = this.#q('source');
    if (source) source.textContent = this.#t.source || '';

    this.#select(featured.id, { announce: false });
  }

  #find(id) {
    return this.#tournaments.find((t) => String(t.id) === String(id));
  }

  // ---------- commands ----------

  #onCommand(e) {
    const source = e.source;
    switch (e.command) {
      // Cards and calendar rows only open the detail panel; the featured tournament never changes on click.
      case '--detail':
        if (source?.dataset.id) this.#toggleDetail(source);
        break;
      case '--close-detail':
        this.#closeDetail({ restoreFocus: true });
        break;
      case '--tab':
        if (source?.dataset.tab) this.#setTab(source.dataset.tab, { focus: false });
        break;
      case '--show-panel': {
        this.#setTab('overview', { focus: false });
        const panel = this.#q('tabpanel');
        if (panel) {
          panel.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
          panel.focus({ preventScroll: true });
        }
        break;
      }
      case '--toggle-calendar':
        this.#calendarOpen = !this.#calendarOpen;
        this.#renderCalendar();
        if (this.#calendarOpen) this.#revealNext();
        break;
      case '--retry':
        this.#load();
        break;
    }
  }

  #onTabKey(e) {
    const tabs = [...this.querySelectorAll('[data-pga="tab"]')];
    const index = tabs.indexOf(document.activeElement);
    if (index < 0) return;
    let next = null;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (e.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = (index + 1) % tabs.length;
    if (e.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = (index - 1 + tabs.length) % tabs.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    this.#setTab(tabs[next].dataset.tab, { focus: true });
  }

  // ---------- selection & featured ----------

  #select(id, { announce }) {
    const t = this.#find(id);
    if (!t) return;
    this.#selectedId = String(t.id);

    this.#renderFeatured(t);
    this.#renderPanel();

    if (announce) this.#announce(`${this.#t.showing || ''} ${t.name}`.trim());
  }

  // ---------- tournament detail (cards + calendar) ----------

  // One detail panel open at a time. `host` is the expanding wrapper: the shared panel under the card row,
  // or the inline slot under a calendar row.
  #toggleDetail(trigger) {
    const id = trigger.dataset.id;
    const host = this.#detailHostFor(trigger);
    if (!host) return;
    if (this.#detail && this.#detail.id === id && this.#detail.host === host) {
      this.#closeDetail({ restoreFocus: false });
      return;
    }
    const t = this.#find(id);
    if (!t) return;

    if (this.#detail && this.#detail.host !== host) this.#closeDetail({ restoreFocus: false, animate: false });

    const body = host.querySelector('[data-slot="body"]');
    if (!body) return;
    body.replaceChildren(this.#detailContent(t));
    this.#detail = { id, host, trigger };
    this.#syncDetailTriggers();

    clearTimeout(this.#detailTimer);
    if (!host.hasAttribute('data-open')) {
      host.hidden = false;
      void host.offsetHeight; // commit the collapsed state so the open transition runs
      host.setAttribute('data-open', '');
      // Bring the panel into view once it has grown; matters most on mobile where it sits below stacked cards.
      this.#detailTimer = setTimeout(() => this.#revealDetail(host), prefersReducedMotion() ? 0 : 320);
    } else {
      this.#revealDetail(host);
    }
    this.#announce(`${this.#t.showing || ''} ${t.name}`.trim());
  }

  #detailHostFor(trigger) {
    if (trigger.dataset.source === 'calendar') return trigger.closest('li')?.querySelector('[data-pga="detail-slot"]');
    return this.#q('detail');
  }

  #closeDetail({ restoreFocus = false, animate = true } = {}) {
    if (!this.#detail) return;
    const { host, trigger } = this.#detail;
    this.#detail = null;
    this.#syncDetailTriggers();
    clearTimeout(this.#detailTimer);
    host.removeAttribute('data-open');
    const finish = () => {
      host.hidden = true;
      host.querySelector('[data-slot="body"]')?.replaceChildren();
    };
    if (!animate || prefersReducedMotion()) finish();
    else this.#detailTimer = setTimeout(finish, 320);
    if (restoreFocus && trigger?.isConnected) trigger.focus();
  }

  #syncDetailTriggers() {
    this.querySelectorAll('[data-pga-detail-trigger]').forEach((btn) => {
      const on = !!this.#detail && btn === this.#detail.trigger;
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  // After "Visa hela spelschemat": scroll the highlighted next tournament to the top (below the sticky header)
  // so the rest of the upcoming season follows beneath it. Focus moves there too for keyboard users.
  #revealNext() {
    const row = this.querySelector('[data-pga="calendar"] [data-pga-next]');
    if (!row) return;
    requestAnimationFrame(() => {
      row.focus({ preventScroll: true });
      row.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    });
  }

  #revealDetail(host) {
    const rect = host.getBoundingClientRect();
    if (rect.bottom > window.innerHeight || rect.top < 0) {
      host.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
    }
  }

  #detailContent(t) {
    const tpl = this.querySelector('template[data-pga-tpl="detail"]');
    const node = tpl ? tpl.content.firstElementChild.cloneNode(true) : el('div');
    node.setAttribute('aria-label', t.name);
    node.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.#closeDetail({ restoreFocus: true });
      }
    });

    const slot = (name) => node.querySelector(`[data-slot="${name}"]`);
    const label = slot('label');
    if (label) label.textContent = t.season ? `${this.#t.season || ''} ${t.season}`.trim() : '';
    const name = slot('name');
    if (name) name.textContent = t.name;
    const status = slot('status');
    if (status) this.#fillPill(status, t._status);

    slot('overview')?.appendChild(
      definitionList([
        [this.#t.dates, formatRange(t.start_date, t.end_date, this.#locale, { day: 'numeric', month: 'long', year: 'numeric' }), ICON_CALENDAR],
        [this.#t.location, locationOf(t), ICON_PIN],
        [this.#t.course, courseName(t), ICON_FLAG],
        [this.#t.purse, clean(t.purse), ICON_PURSE],
        [this.#t.season, t.season ? String(t.season) : '', ICON_TROPHY],
      ])
    );

    const courseHeading = slot('course-heading');
    if (courseHeading) courseHeading.textContent = this.#t.course || '';
    slot('course')?.appendChild(this.#renderCourse(t));

    const champ = t.champion;
    const champName = t._status === 'completed' && champ && (clean(champ.display_name) || [clean(champ.first_name), clean(champ.last_name)].filter(Boolean).join(' '));
    const winner = slot('winner');
    if (winner && champName) {
      winner.hidden = false;
      const heading = slot('winner-heading');
      if (heading) heading.textContent = this.#t.winner || '';
      const winnerName = slot('winner-name');
      if (winnerName) winnerName.textContent = champName;
      const country = slot('winner-country');
      if (country) country.textContent = clean(champ.country);
    }

    const close = slot('close');
    if (close) close.setAttribute('commandfor', this.id);
    return node;
  }

  // One of up to three variants per location scene, picked from the tournament id so a tournament always
  // gets the same image while neighbouring events in the same category usually differ.
  #sceneImage(t) {
    const raw = this.#scenes[sceneFor(t)];
    const list = (Array.isArray(raw) ? raw : [raw]).filter(Boolean);
    if (!list.length) return null;
    const id = String(t.id ?? t.name ?? '');
    const n = /^\d+$/.test(id) ? Number(id) : [...id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
    return list[n % list.length];
  }

  #renderFeatured(t) {
    const label = this.#q('featured-label');
    if (label) label.textContent = this.#t[this.#featuredReason] || '';

    const title = this.#q('featured-title');
    if (title) title.textContent = t.name;

    const meta = this.#q('featured-meta');
    if (meta) {
      meta.replaceChildren();
      const dates = formatRange(t.start_date, t.end_date, this.#locale, { day: 'numeric', month: 'long' });
      if (dates) meta.appendChild(metaItem(ICON_CALENDAR, dates));
      const course = courseName(t);
      if (course) meta.appendChild(metaItem(ICON_FLAG, course));
      const loc = locationOf(t);
      if (loc) meta.appendChild(metaItem(ICON_PIN, loc));
    }

    // Same destination illustration as the schedule cards; the template's line-drawn green stays as fallback.
    const art = this.#q('featured-art');
    const sceneSrc = this.#sceneImage(t);
    if (art) {
      if (sceneSrc && art.getAttribute('src') !== sceneSrc) art.src = sceneSrc;
      art.hidden = !sceneSrc;
    }
    this.#q('featured-svg')?.toggleAttribute('hidden', !!sceneSrc);

    // Discreet status in the frame's top-right corner; label comes from the API status (Pågår / Kommande / …).
    const status = this.#q('featured-status');
    if (status) {
      status.textContent = this.#t[`status_${t._status}`] || '';
      status.className = 'inline-flex shrink-0 items-center gap-2 rounded-full bg-(--pga-status-bg) px-3 py-1 text-xs font-medium text-(--pga-status-text)';
      if (t._status === 'ongoing') {
        const dot = el('span', 'size-1.5 shrink-0 rounded-full bg-[#3E8E5E] motion-safe:animate-pulse');
        dot.setAttribute('aria-hidden', 'true');
        status.prepend(dot);
      }
      status.hidden = !status.textContent;
    }

    const cta = this.#q('featured-cta');
    if (cta) cta.removeAttribute('aria-disabled');
  }

  #fillPill(el, status, extra = '') {
    el.textContent = this.#t[`status_${status}`] || '';
    el.className = `inline-flex shrink-0 items-center gap-2 rounded-full bg-surface-secondary px-3 py-1 text-xs font-medium text-on-surface ${extra}`.trim();
    el.prepend(statusDot(status));
    el.hidden = false;
  }

  // ---------- panel & tabs ----------

  #setTab(tab, { focus }) {
    this.#activeTab = tab;
    this.querySelectorAll('[data-pga="tab"]').forEach((btn) => {
      const on = btn.dataset.tab === tab;
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
      btn.tabIndex = on ? 0 : -1;
      if (on && focus) btn.focus();
    });
    const panel = this.#q('tabpanel');
    const activeBtn = this.querySelector(`[data-pga="tab"][data-tab="${tab}"]`);
    if (panel && activeBtn?.id) panel.setAttribute('aria-labelledby', activeBtn.id);
    this.#renderPanel();
  }

  // Each tab is a renderer; a future live-results tab (e.g. "results") only needs a tab button and an entry here.
  #tabRenderers = {
    overview: (t) => this.#renderOverview(t),
    course: (t) => this.#renderCourse(t, { compact: true }),
    winner: (t) => this.#renderWinner(t),
  };

  #renderPanel() {
    const panel = this.#q('tabpanel');
    const t = this.#find(this.#selectedId);
    if (!panel || !t) return;

    const panelStatus = this.#q('panel-status');
    if (panelStatus) this.#fillPill(panelStatus, t._status);

    const tab = this.#tabRenderers[this.#activeTab] ? this.#activeTab : 'overview';
    const content = this.#tabRenderers[tab](t);
    content.classList.add('motion-safe:animate-[pga-fade_300ms_var(--ease-ui)]', '[grid-area:1/1]');

    // Every tab is stacked invisibly in the same grid cell, so the panel is always as tall as the
    // tallest tab: switching tabs never resizes the featured card and nothing ever needs to scroll.
    const stack = el('div', 'grid');
    Object.entries(this.#tabRenderers).forEach(([key, render]) => {
      if (key === tab) return;
      const sizer = render(t);
      sizer.classList.add('invisible', '[grid-area:1/1]');
      sizer.setAttribute('aria-hidden', 'true');
      sizer.inert = true;
      stack.appendChild(sizer);
    });
    stack.appendChild(content);
    panel.replaceChildren(stack);
    panel.removeAttribute('aria-busy');
  }

  #renderOverview(t) {
    const wrap = el('div', 'grid');
    const statusLabel = this.#t[`status_${t._status}`];
    const rows = [
      [this.#t.dates, formatRange(t.start_date, t.end_date, this.#locale, { day: 'numeric', month: 'long', year: 'numeric' }), ICON_CALENDAR],
      [this.#t.location, locationOf(t), ICON_PIN],
      [this.#t.course, courseName(t), ICON_FLAG],
      [this.#t.status, statusLabel, ICON_STATUS, statusLabel ? statusDot(t._status) : null],
      [this.#t.purse, clean(t.purse), ICON_PURSE],
      [this.#t.season, t.season ? String(t.season) : '', ICON_TROPHY],
    ];
    wrap.appendChild(definitionList(rows));
    return wrap;
  }

  #renderCourse(t, { compact = false } = {}) {
    const wrap = el('div', 'grid gap-8');
    const courses = Array.isArray(t.courses) ? t.courses.map((c) => c && (c.course || c)).filter(Boolean) : [];

    if (!courses.length) {
      const name = courseName(t);
      if (name) wrap.appendChild(el('p', 'font-heading text-2xl leading-tight md:text-[1.75rem]', name));
      wrap.appendChild(el('p', 'type-body-sm text-on-surface-secondary', this.#t.course_missing));
      return wrap;
    }

    // In the featured tab (compact) facts sit in two columns so the course fits inside the overview's height;
    // the card detail panel keeps the original one-column list.
    if (compact) wrap.className = 'grid gap-6';
    courses.forEach((c) => {
      const block = el('div', compact ? 'grid gap-3' : 'grid gap-5');
      if (clean(c.name)) block.appendChild(el('p', compact ? 'font-heading text-[1.375rem] leading-tight md:text-2xl' : 'font-heading text-2xl leading-tight md:text-[1.75rem]', c.name));
      const yardage = clean(c.yardage);
      block.appendChild(
        (compact ? compactFacts : definitionList)([
          [this.#t.par, c.par != null ? String(c.par) : ''],
          [this.#t.yardage, yardage ? `${yardage.replace(/,/g, '\u00a0')} ${this.#t.yards || ''}`.trim() : ''],
          [this.#t.architect, clean(c.architect)?.replace(/\\/g, ', ')],
          [this.#t.established, clean(c.established)],
          [this.#t.fairway_grass, clean(c.fairway_grass)],
          [this.#t.green_grass, clean(c.green_grass)],
          [this.#t.location, [clean(c.city), clean(c.state), clean(c.country)].filter(Boolean).join(', ')],
        ])
      );
      wrap.appendChild(block);
    });
    return wrap;
  }

  #renderWinner(t) {
    const wrap = el('div', 'grid gap-5');
    const champ = t.champion;
    if (t._status !== 'completed') {
      wrap.appendChild(el('p', 'type-body-sm text-on-surface-secondary', this.#t.winner_pending));
      return wrap;
    }
    const name = champ && (clean(champ.display_name) || [clean(champ.first_name), clean(champ.last_name)].filter(Boolean).join(' '));
    if (!name) {
      wrap.appendChild(el('p', 'type-body-sm text-on-surface-secondary', this.#t.winner_missing));
      return wrap;
    }
    wrap.appendChild(el('p', 'type-overline text-accent', this.#t.winner));
    wrap.appendChild(el('p', 'font-heading text-3xl leading-tight md:text-[2.25rem]', name));
    wrap.appendChild(
      definitionList([
        [this.#t.country, clean(champ.country)],
        [this.#t.dates, formatRange(t.start_date, t.end_date, this.#locale, { day: 'numeric', month: 'long', year: 'numeric' })],
        [this.#t.course, courseName(t)],
      ])
    );
    return wrap;
  }

  // ---------- cards ----------

  #renderCards(list) {
    const grid = this.#q('schedule');
    if (!grid) return;
    this.#closeDetail({ animate: false });
    grid.replaceChildren();
    grid.removeAttribute('aria-busy');

    if (!list.length) {
      grid.appendChild(el('p', 'type-body-sm text-on-surface-secondary sm:col-span-2 lg:col-span-4', this.#t.empty));
      return;
    }

    list.forEach((t, index) => {
      const card = el(
        'button',
        'group relative isolate flex h-full min-h-[10.5rem] flex-col overflow-hidden rounded-card border border-border bg-surface py-4 ps-7 pe-4 text-start cursor-pointer motion-safe:transition-[border-color,box-shadow,transform] motion-safe:duration-300 ease-ui hover:border-on-surface/30 hover:shadow aria-expanded:border-on-surface aria-expanded:shadow motion-safe:active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
      );
      // Accent strips cycle through the block's four colour settings.
      card.style.setProperty('--pga-accent', `var(--pga-accent-${(index % 4) + 1})`);
      card.type = 'button';
      card.dataset.id = String(t.id);
      card.dataset.pgaCard = '';
      card.setAttribute('commandfor', this.id);
      card.setAttribute('command', '--detail');
      card.dataset.pgaDetailTrigger = '';
      card.dataset.source = 'cards';
      card.setAttribute('aria-expanded', 'false');
      if (this.#q('detail')?.id) card.setAttribute('aria-controls', this.#q('detail').id);
      card.setAttribute('aria-label', `${this.#t.show || ''} ${t.name}`.trim());

      const dateText = formatRange(t.start_date, t.end_date, this.#locale, { day: 'numeric', month: 'short', year: 'numeric' }).replace(/\./g, '');
      const strip = el('span', 'absolute inset-y-0 start-0 w-2.5 bg-(--pga-accent)');
      strip.setAttribute('aria-hidden', 'true');
      card.appendChild(strip);

      // Illustration: the merchant's scene image when set (block settings / shop files), otherwise the inline line-art.
      const scene = sceneFor(t);
      const sceneSrc = this.#sceneImage(t);
      if (sceneSrc) {
        const art = el('img', 'pointer-events-none absolute inset-0 -z-10 size-full object-cover object-right-bottom opacity-[0.34] mix-blend-multiply motion-safe:transition-opacity motion-safe:duration-300 ease-ui group-hover:opacity-[0.5]');
        art.src = sceneSrc;
        art.alt = '';
        art.loading = 'lazy';
        art.decoding = 'async';
        art.width = 900;
        art.height = 600;
        art.dataset.scene = scene;
        card.appendChild(art);
      } else {
        const art = el('span', 'pointer-events-none absolute inset-y-2 end-9 -z-10 w-[68%] text-on-surface opacity-[0.15] motion-safe:transition-opacity motion-safe:duration-300 ease-ui group-hover:opacity-[0.22]');
        art.setAttribute('aria-hidden', 'true');
        art.dataset.scene = scene;
        art.insertAdjacentHTML('afterbegin', sceneSvg(scene));
        card.appendChild(art);
      }

      card.appendChild(el('span', 'text-[0.6875rem] uppercase leading-relaxed tracking-[0.1em] text-on-surface-secondary tabular-nums', dateText));
      card.appendChild(el('span', 'mt-2 line-clamp-3 pe-2 font-heading text-[1.1875rem] leading-snug', t.name));

      const footer = el('span', 'mt-auto flex items-end justify-between gap-3 pt-5');
      const loc = locationOf(t);
      const locEl = el('span', 'flex min-w-0 items-start gap-1.5 text-[0.6875rem] uppercase leading-snug tracking-[0.08em] text-on-surface-secondary');
      if (loc) {
        locEl.insertAdjacentHTML('afterbegin', ICON_PIN);
        locEl.appendChild(el('span', 'line-clamp-2', loc));
      }
      footer.appendChild(locEl);

      const arrow = el('span', 'inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-on-surface/50 bg-surface text-on-surface motion-safe:transition-[background-color,color,border-color,transform] motion-safe:duration-300 ease-ui group-hover:border-on-surface group-aria-expanded:rotate-90 group-aria-expanded:border-on-surface group-aria-expanded:bg-on-surface group-aria-expanded:text-surface');
      arrow.insertAdjacentHTML('afterbegin', ICON_ARROW);
      footer.appendChild(arrow);
      card.appendChild(footer);

      grid.appendChild(card);
    });
  }

  // ---------- calendar ----------

  #renderCalendar() {
    const region = this.#q('calendar');
    const toggle = this.#q('schedule-toggle');
    if (!region) return;

    if (toggle) {
      toggle.setAttribute('aria-expanded', this.#calendarOpen ? 'true' : 'false');
      const label = toggle.querySelector('[data-pga="toggle-label"]');
      if (label) label.textContent = this.#calendarOpen ? this.#t.toggle_close : this.#t.toggle_open;
    }

    // A calendar row's inline panel is rebuilt with the calendar, so drop it before re-rendering or collapsing.
    if (this.#detail?.trigger?.dataset.source === 'calendar') this.#closeDetail({ animate: false });

    region.hidden = !this.#calendarOpen;
    if (!this.#calendarOpen) return;

    region.replaceChildren();
    const season = this.#tournaments.find((t) => t.season)?.season;
    region.appendChild(el('h3', 'type-overline mb-4 text-on-surface-secondary md:text-xs', `${this.#t.calendar_heading || ''} ${season || ''}`.trim()));

    const monthFmt = new Intl.DateTimeFormat(this.#locale, { month: 'long', timeZone: 'UTC' });
    let currentMonth = null;
    let list = null;

    this.#tournaments.forEach((t, i) => {
      const month = monthFmt.format(isoToDate(t.start_date));
      if (month !== currentMonth) {
        currentMonth = month;
        region.appendChild(el('p', 'mt-6 border-b border-on-surface pb-2 font-heading text-xl first-letter:uppercase md:mt-8 md:text-[1.75rem]', month));
        list = el('ul', 'divide-y divide-border');
        list.setAttribute('role', 'list');
        region.appendChild(list);
      }

      const li = document.createElement('li');
      const row = el(
        'button',
        'group grid w-full grid-cols-[4.5rem_1fr_auto] items-center gap-4 py-3 text-start cursor-pointer motion-safe:transition-colors hover:bg-surface-secondary/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:grid-cols-[6rem_1fr_auto_auto] md:grid-cols-[8rem_1fr_auto_auto] md:gap-6 md:py-4'
      );
      row.type = 'button';
      row.dataset.id = String(t.id);
      row.setAttribute('commandfor', this.id);
      row.setAttribute('command', '--detail');
      row.dataset.pgaDetailTrigger = '';
      row.dataset.source = 'calendar';
      row.setAttribute('aria-expanded', 'false');
      row.setAttribute('aria-controls', `${this.id}-cal-detail-${String(t.id).replace(/[^\w-]/g, '')}`);
      row.setAttribute('aria-label', `${this.#t.show || ''} ${t.name}`.trim());
      if (t._status === 'completed') row.classList.add('text-on-surface-secondary');

      row.appendChild(el('span', 'text-xs tabular-nums md:text-sm', formatRange(t.start_date, t.end_date, this.#locale, { day: 'numeric', month: 'short' })));
      const isNext = this.#nextId != null && String(t.id) === String(this.#nextId);
      if (isNext) {
        // Highlighted "next" row: tinted band that bleeds past the column edges, a forest-green rule on the start edge, and a label above the name.
        li.className = 'relative -mx-3 bg-surface-secondary before:absolute before:inset-y-0 before:start-0 before:w-0.5 before:bg-interactive';
        row.classList.add('px-3', 'scroll-mt-32');
        row.dataset.pgaNext = '';
      }

      const main = el('span', 'flex min-w-0 flex-col');
      if (isNext) {
        const nextLabel = t._status === 'ongoing' ? this.#t.label_ongoing : this.#t.label_next;
        main.appendChild(el('span', 'mb-0.5 text-[0.625rem] font-medium uppercase tracking-[0.14em] text-accent md:text-[0.6875rem]', nextLabel));
      }
      main.appendChild(el('span', 'truncate text-sm font-medium text-on-surface md:text-[1.0625rem]', t.name));
      const sub = [locationOf(t)];
      const champ = t._status === 'completed' && t.champion && clean(t.champion.display_name);
      if (champ) sub.push(`${this.#t.winner}: ${champ}`);
      main.appendChild(el('span', 'truncate text-xs text-on-surface-secondary md:mt-0.5 md:text-sm', sub.filter(Boolean).join(' · ')));
      row.appendChild(main);

      const pill = el('span', `hidden rounded-button px-2.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-[0.08em] sm:inline-flex md:px-3 md:py-1 md:text-[0.6875rem] ${STATUS_PILL[t._status] || STATUS_PILL.unknown}`, this.#t[`status_${t._status}`]);
      row.appendChild(pill);
      const arrow = el('span', 'text-on-surface motion-safe:transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 group-aria-expanded:translate-x-0 group-aria-expanded:rotate-90');
      arrow.insertAdjacentHTML('afterbegin', ICON_ARROW);
      row.appendChild(arrow);

      li.appendChild(row);
      const slotTpl = this.querySelector('template[data-pga-tpl="detail-slot"]');
      if (slotTpl) {
        const slot = slotTpl.content.firstElementChild.cloneNode(true);
        slot.id = row.getAttribute('aria-controls');
        li.appendChild(slot);
      }
      list.appendChild(li);
    });
  }

  // ---------- states ----------

  #renderLoading() {
    this.#closeDetail({ animate: false });
    const panel = this.#q('tabpanel');
    if (panel) {
      panel.setAttribute('aria-busy', 'true');
      panel.replaceChildren(skeleton(this.#t.loading, 5));
    }
    const grid = this.#q('schedule');
    if (grid) {
      grid.setAttribute('aria-busy', 'true');
      grid.replaceChildren();
      for (let i = 0; i < 4; i++) {
        const card = el('div', 'relative grid min-h-[10.5rem] content-start gap-3 overflow-hidden rounded-card border border-border bg-surface py-4 ps-7 pe-4');
        card.setAttribute('aria-hidden', 'true');
        card.appendChild(el('span', 'absolute inset-y-0 start-0 w-2.5 bg-surface-secondary'));
        ['w-1/2', 'w-4/5', 'w-2/3'].forEach((w) => card.appendChild(el('span', `block h-3 ${w} bg-surface-secondary motion-safe:animate-pulse`)));
        grid.appendChild(card);
      }
    }
    const title = this.#q('featured-title');
    if (title) title.textContent = this.#t.loading || '';
  }

  #renderError() {
    this.#closeDetail({ animate: false });
    const message = () => {
      const box = el('div', 'grid justify-items-start gap-4');
      box.setAttribute('role', 'alert');
      box.appendChild(el('p', 'type-body-sm', this.#t.error));
      const retry = el('button', 'button-outline inline-flex h-10 items-center rounded-button px-5 text-sm font-medium cursor-pointer motion-safe:active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent', this.#t.retry);
      retry.type = 'button';
      retry.setAttribute('commandfor', this.id);
      retry.setAttribute('command', '--retry');
      box.appendChild(retry);
      return box;
    };
    const panel = this.#q('tabpanel');
    if (panel) {
      panel.removeAttribute('aria-busy');
      panel.replaceChildren(message());
    }
    const grid = this.#q('schedule');
    if (grid) {
      grid.removeAttribute('aria-busy');
      grid.replaceChildren(el('p', 'type-body-sm text-on-surface-secondary sm:col-span-2 lg:col-span-3', this.#t.error));
    }
    const title = this.#q('featured-title');
    if (title) title.textContent = '';
    const label = this.#q('featured-label');
    if (label) label.textContent = '';
    const meta = this.#q('featured-meta');
    if (meta) meta.replaceChildren();
    this.#q('featured-status')?.setAttribute('hidden', '');
    this.#q('panel-status')?.setAttribute('hidden', '');
    const toggle = this.#q('schedule-toggle');
    if (toggle) toggle.hidden = true;
  }

  #renderEmpty() {
    const panel = this.#q('tabpanel');
    if (panel) {
      panel.removeAttribute('aria-busy');
      panel.replaceChildren(el('p', 'type-body-sm text-on-surface-secondary', this.#t.empty));
    }
    this.#renderCards([]);
    const title = this.#q('featured-title');
    if (title) title.textContent = '';
    const toggle = this.#q('schedule-toggle');
    if (toggle) toggle.hidden = true;
  }

  #announce(text) {
    if (!this.#live) return;
    this.#live.textContent = '';
    requestAnimationFrame(() => {
      this.#live.textContent = text;
    });
  }
}

// ---------- helpers (pure; all API text goes through textContent) ----------

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null && text !== '') node.textContent = String(text);
  return node;
}

function metaItem(icon, text) {
  const li = el('li', 'flex items-center gap-2');
  li.insertAdjacentHTML('afterbegin', icon);
  li.appendChild(el('span', '', text));
  return li;
}

// rows: [label, value, icon?, leadingNode?]. Icons sit in a quiet column before the label.
function definitionList(rows) {
  const dl = el('dl', 'grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-6 text-sm');
  rows
    .filter(([label, value]) => label && value)
    .forEach(([label, value, icon, leading]) => {
      const dt = el('dt', 'flex items-center gap-4 border-b border-border py-3 type-overline text-[0.625rem] tracking-[0.12em] text-on-surface-secondary');
      if (icon) {
        const iconWrap = el('span', 'inline-flex w-5 justify-center text-on-surface');
        iconWrap.insertAdjacentHTML('afterbegin', icon);
        dt.appendChild(iconWrap);
      }
      dt.appendChild(el('span', '', label));
      dl.appendChild(dt);
      const dd = el('dd', 'flex items-center gap-2 border-b border-border py-3 text-on-surface', value);
      if (leading) dd.prepend(leading);
      dl.appendChild(dd);
    });
  return dl;
}

// rows: [label, value]. Two-column label-over-value grid for dense fact sets (course tab).
function compactFacts(rows) {
  const dl = el('dl', 'grid grid-cols-2 gap-x-6');
  rows
    .filter(([label, value]) => label && value)
    .forEach(([label, value]) => {
      const item = el('div', 'grid content-start gap-0.5 border-b border-border py-2.5');
      item.appendChild(el('dt', 'type-overline text-[0.625rem] tracking-[0.12em] text-on-surface-secondary', label));
      item.appendChild(el('dd', 'text-sm leading-snug text-on-surface', value));
      dl.appendChild(item);
    });
  return dl;
}

function statusDot(status) {
  const dot = el('span', `size-1.5 shrink-0 rounded-full ${status === 'ongoing' ? 'bg-accent motion-safe:animate-pulse' : status === 'upcoming' ? 'bg-on-surface' : 'bg-on-surface-secondary'}`);
  dot.setAttribute('aria-hidden', 'true');
  return dot;
}

function skeleton(label, lines) {
  const wrap = el('div', 'grid gap-3');
  wrap.appendChild(el('p', 'sr-only', label));
  const widths = ['w-2/3', 'w-full', 'w-5/6', 'w-3/4', 'w-1/2'];
  for (let i = 0; i < lines; i++) {
    const bar = el('span', `block h-4 ${widths[i % widths.length]} bg-surface-secondary motion-safe:animate-pulse`);
    bar.setAttribute('aria-hidden', 'true');
    wrap.appendChild(bar);
  }
  return wrap;
}

function clean(value) {
  if (value == null) return '';
  const s = String(value).trim();
  return s && s.toLowerCase() !== 'null' ? s : '';
}

function statusOf(t) {
  const s = clean(t.status).toUpperCase();
  const state = clean(t.status_state).toLowerCase();
  if (s === 'COMPLETED' || s === 'FINAL' || state === 'final' || state === 'post') return 'completed';
  if (s === 'NOT_STARTED' || s === 'SCHEDULED' || state === 'scheduled' || state === 'pre') return 'upcoming';
  if (/CANCEL|POSTPONE/.test(s) || state === 'canceled' || state === 'cancelled') return 'canceled';
  // Only an explicit in-play signal from the API counts as ongoing; dates alone never do.
  if (/IN_PROGRESS|INPROGRESS|ACTIVE|LIVE|PLAYING/.test(s) || state === 'in' || state === 'in_progress') return 'ongoing';
  return 'unknown';
}

function courseName(t) {
  const fromCourses = Array.isArray(t.courses) && t.courses[0] && (t.courses[0].course || t.courses[0]).name;
  return clean(t.course_name) || clean(fromCourses);
}

function locationOf(t) {
  let country = clean(t.country);
  if (/^United States/i.test(country)) country = 'USA';
  return [clean(t.city), clean(t.state), country].filter(Boolean).join(', ');
}

function isIsoDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value);
}

function isoToDate(iso) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function todayIso() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatRange(start, end, locale, options) {
  if (!isIsoDate(start)) return '';
  try {
    const fmt = new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
    const a = isoToDate(start);
    if (!isIsoDate(end) || end.slice(0, 10) === start.slice(0, 10)) return fmt.format(a);
    const b = isoToDate(end);
    return typeof fmt.formatRange === 'function' ? fmt.formatRange(a, b) : `${fmt.format(a)} – ${fmt.format(b)}`;
  } catch {
    return start;
  }
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

if (!customElements.get('pga-tour-central')) {
  customElements.define('pga-tour-central', PgaTourCentral);
}
